import React, { useState, useRef, useEffect } from 'react';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import Icon from './AppIcon';

const RichTextEditor = ({ 
  value = '', 
  onChange, 
  placeholder = "Start writing...",
  showPreview = true,
  minHeight = "300px"
}) => {
  const { currentColors } = useAdminTheme();
  const [activeTab, setActiveTab] = useState('write');
  const [selectedText, setSelectedText] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef(null);

  const insertText = (before, after = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    const newText = value.substring(0, start) + before + selectedText + after + value.substring(end);
    
    onChange(newText);
    
    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + before.length + selectedText.length + after.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const formatText = (format) => {
    switch (format) {
      case 'bold':
        insertText('**', '**');
        break;
      case 'italic':
        insertText('*', '*');
        break;
      case 'heading1':
        insertText('# ');
        break;
      case 'heading2':
        insertText('## ');
        break;
      case 'heading3':
        insertText('### ');
        break;
      case 'quote':
        insertText('> ');
        break;
      case 'code':
        insertText('`', '`');
        break;
      case 'codeblock':
        insertText('\n```\n', '\n```\n');
        break;
      case 'link':
        insertText('[', '](url)');
        break;
      case 'image':
        insertText('![alt text](', ')');
        break;
      case 'list':
        insertText('- ');
        break;
      case 'numberlist':
        insertText('1. ');
        break;
      case 'hr':
        insertText('\n---\n');
        break;
    }
  };

  const insertTable = () => {
    const table = `
| Header 1 | Header 2 | Header 3 |
|----------|----------|----------|
| Cell 1   | Cell 2   | Cell 3   |
| Cell 4   | Cell 5   | Cell 6   |
`;
    insertText(table);
  };

  const renderMarkdown = (text) => {
    return text
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      .replace(/\*\*(.*)\*\*/gim, '<strong>$1</strong>')
      .replace(/\*(.*)\*/gim, '<em>$1</em>')
      .replace(/!\[([^\]]*)\]\(([^\)]*)\)/gim, '<img src="$2" alt="$1" style="max-width: 100%; height: auto;" />')
      .replace(/\[([^\]]*)\]\(([^\)]*)\)/gim, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
      .replace(/`([^`]*)`/gim, '<code style="background: #f1f1f1; padding: 2px 4px; border-radius: 3px;">$1</code>')
      .replace(/^> (.*$)/gim, '<blockquote style="border-left: 4px solid #ddd; margin: 0; padding-left: 16px; color: #666;">$1</blockquote>')
      .replace(/^- (.*$)/gim, '<li>$1</li>')
      .replace(/^(\d+)\. (.*$)/gim, '<li>$1. $2</li>')
      .replace(/\n/gim, '<br/>');
  };

  const toolbarButtons = [
    { name: 'bold', icon: 'Bold', tooltip: 'Bold (Ctrl+B)' },
    { name: 'italic', icon: 'Italic', tooltip: 'Italic (Ctrl+I)' },
    { type: 'separator' },
    { name: 'heading1', icon: 'Type', tooltip: 'Heading 1' },
    { name: 'heading2', icon: 'Type', tooltip: 'Heading 2' },
    { name: 'heading3', icon: 'Type', tooltip: 'Heading 3' },
    { type: 'separator' },
    { name: 'quote', icon: 'Quote', tooltip: 'Quote' },
    { name: 'code', icon: 'Code', tooltip: 'Inline Code' },
    { name: 'codeblock', icon: 'FileCode', tooltip: 'Code Block' },
    { type: 'separator' },
    { name: 'link', icon: 'Link', tooltip: 'Insert Link' },
    { name: 'image', icon: 'Image', tooltip: 'Insert Image' },
    { type: 'separator' },
    { name: 'list', icon: 'List', tooltip: 'Bullet List' },
    { name: 'numberlist', icon: 'ListOrdered', tooltip: 'Numbered List' },
    { name: 'table', icon: 'Table', tooltip: 'Insert Table' },
    { name: 'hr', icon: 'Minus', tooltip: 'Horizontal Rule' },
  ];

  return (
    <div className="rich-text-editor" style={{ border: `1px solid ${currentColors.border}`, borderRadius: '8px', overflow: 'hidden' }}>
      {/* Toolbar */}
      <div className="toolbar" style={{ 
        background: currentColors.cardBackground, 
        borderBottom: `1px solid ${currentColors.border}`,
        padding: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap'
      }}>
        {toolbarButtons.map((button, index) => {
          if (button.type === 'separator') {
            return (
              <div 
                key={index}
                style={{
                  width: '1px',
                  height: '24px',
                  backgroundColor: currentColors.border,
                  margin: '0 4px'
                }}
              />
            );
          }
          
          return (
            <button
              key={button.name}
              onClick={() => button.name === 'table' ? insertTable() : formatText(button.name)}
              title={button.tooltip}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '8px',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                color: currentColors.text
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = currentColors.hover}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              <Icon name={button.icon} size={16} />
            </button>
          );
        })}

        {showPreview && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
            <button
              onClick={() => setActiveTab('write')}
              style={{
                background: activeTab === 'write' ? currentColors.primary : 'transparent',
                color: activeTab === 'write' ? '#ffffff' : currentColors.text,
                border: `1px solid ${currentColors.border}`,
                padding: '6px 12px',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Write
            </button>
            <button
              onClick={() => setActiveTab('preview')}
              style={{
                background: activeTab === 'preview' ? currentColors.primary : 'transparent',
                color: activeTab === 'preview' ? '#ffffff' : currentColors.text,
                border: `1px solid ${currentColors.border}`,
                padding: '6px 12px',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Preview
            </button>
          </div>
        )}
      </div>

      {/* Editor/Preview Area */}
      <div style={{ minHeight, position: 'relative' }}>
        {activeTab === 'write' || !showPreview ? (
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            style={{
              width: '100%',
              minHeight,
              padding: '16px',
              border: 'none',
              outline: 'none',
              resize: 'vertical',
              fontFamily: 'SF Mono, Monaco, Consolas, "Roboto Mono", monospace',
              fontSize: '14px',
              lineHeight: '1.6',
              backgroundColor: currentColors.background,
              color: currentColors.text,
              caretColor: currentColors.primary
            }}
            onKeyDown={(e) => {
              if (e.ctrlKey || e.metaKey) {
                if (e.key === 'b') {
                  e.preventDefault();
                  formatText('bold');
                } else if (e.key === 'i') {
                  e.preventDefault();
                  formatText('italic');
                } else if (e.key === 'k') {
                  e.preventDefault();
                  formatText('link');
                }
              }
            }}
          />
        ) : (
          <div
            style={{
              padding: '16px',
              minHeight,
              backgroundColor: currentColors.background,
              color: currentColors.text,
              fontSize: '14px',
              lineHeight: '1.6',
              overflow: 'auto'
            }}
            dangerouslySetInnerHTML={{
              __html: renderMarkdown(value) || '<p style="color: #999; font-style: italic;">Nothing to preview...</p>'
            }}
          />
        )}
      </div>

      {/* Status Bar */}
      <div style={{
        background: currentColors.cardBackground,
        borderTop: `1px solid ${currentColors.border}`,
        padding: '8px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '12px',
        color: currentColors.textSecondary
      }}>
        <div>
          {value.length} characters, {value.split(/\s+/).filter(word => word.length > 0).length} words
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <span>Markdown supported</span>
          <span>Ctrl+B Bold, Ctrl+I Italic</span>
        </div>
      </div>
    </div>
  );
};

export default RichTextEditor;
